import type { ReactNode } from 'react';
import { ReportSection, type SectionState } from '@/components/ReportSection';
import { USAGE_ANALYTICS_COPY } from '../copy';
import { SectionFailure } from './SectionFailure';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ONE SECTION'S SHELL, AND ITS THREE STATES. [ANALYTICS §5]
 *
 *  ⚠️ [amended P7C §2] THE SHELL IS `components/ReportSection.tsx` NOW, promoted on its second user
 *  (Duty analytics). This keeps Usage analytics' name, its words and its failure sentence, and
 *  passes no `actions`, so the DOM is the one this page has always rendered — the reasons the three
 *  states exist are written there, where the code is.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type { SectionState };

export const AnalyticsSection = <T,>({
  id,
  title,
  state,
  children,
}: {
  id: string;
  title: string;
  state: SectionState<T>;
  children: (data: T) => ReactNode;
}) => (
  <ReportSection
    id={id}
    testIdPrefix="analytics"
    title={title}
    state={state}
    loadingLabel={USAGE_ANALYTICS_COPY.loadingSection(title)}
    failure={<SectionFailure />}
  >
    {children}
  </ReportSection>
);

/** A labelled sub-block inside a section: a small heading, an optional caption, and its body. */
export const SubBlock = ({ title, caption, children }: { title: string; caption?: string; children: ReactNode }) => (
  <div className="flex min-w-0 flex-col gap-2">
    <div className="flex flex-col gap-0.5">
      <h3 className="text-sm font-medium text-foreground">{title}</h3>
      {caption ? <p className="text-xs text-muted-foreground">{caption}</p> : null}
    </div>
    {children}
  </div>
);
